export const ARCHITECTURE_STANDARD_ENTITY_CLASS = "architecture-standard";

export const ARCHITECTURE_ARTIFACT_TYPES = [
  "microservice",
  "apigee-api-proxy",
  "apigee-shared-flow",
  "api-management",
  "kubernetes-cluster",
  "kubernetes-namespace",
  "kubernetes-workload",
] as const;

export type ArchitectureArtifactType =
  (typeof ARCHITECTURE_ARTIFACT_TYPES)[number];

export const ARCHITECTURE_LIFECYCLE_PHASES = [
  "design",
  "development",
  "testing",
  "documentation",
  "configuration",
  "release",
  "decommissioning",
] as const;

export type ArchitectureLifecyclePhase =
  (typeof ARCHITECTURE_LIFECYCLE_PHASES)[number];

export const ARCHITECTURE_ARTIFACT_LABELS: Record<
  ArchitectureArtifactType,
  string
> = {
  microservice: "Microservice",
  "apigee-api-proxy": "Apigee API proxy",
  "apigee-shared-flow": "Apigee shared flow",
  "api-management": "API management",
  "kubernetes-cluster": "Kubernetes cluster",
  "kubernetes-namespace": "Kubernetes namespace",
  "kubernetes-workload": "Kubernetes workload",
};

export const ARCHITECTURE_PHASE_LABELS: Record<
  ArchitectureLifecyclePhase,
  string
> = {
  design: "Design",
  development: "Development",
  testing: "Testing",
  documentation: "Documentation",
  configuration: "Configuration",
  release: "Release",
  decommissioning: "Decommissioning",
};

export interface ArchitectureStandardClassifications {
  artifactType?: string;
  lifecyclePhase?: string;
  standardVersion?: string;
}

export function isArchitectureStandardDocument(doc: {
  entityClass?: string | null;
  tags?: string[] | null;
}): boolean {
  if (doc.entityClass === ARCHITECTURE_STANDARD_ENTITY_CLASS) return true;
  return Boolean(doc.tags?.includes(ARCHITECTURE_STANDARD_ENTITY_CLASS));
}

export function parseArchitectureClassifications(
  raw: string | null | undefined,
): ArchitectureStandardClassifications {
  if (!raw?.trim()) return {};
  try {
    const parsed = JSON.parse(raw) as ArchitectureStandardClassifications;
    return parsed && typeof parsed === "object" ? parsed : {};
  } catch {
    return {};
  }
}

export function architectureClassificationsJson(
  artifactType: string,
  lifecyclePhase: string,
  standardVersion = "1.0.0",
): string {
  return JSON.stringify({ artifactType, lifecyclePhase, standardVersion });
}

export function architectureStandardName(
  artifactType: string,
  lifecyclePhase: string,
): string {
  const artifact =
    ARCHITECTURE_ARTIFACT_LABELS[artifactType as ArchitectureArtifactType] ??
    artifactType;
  const phase =
    ARCHITECTURE_PHASE_LABELS[lifecyclePhase as ArchitectureLifecyclePhase] ??
    lifecyclePhase;
  return `${artifact} — ${phase}`;
}

export function architectureIdentityKey(
  artifactType: string,
  lifecyclePhase: string,
): string {
  return `${artifactType}|${lifecyclePhase}`;
}

export const ARCHITECTURE_STANDARD_CELL_COUNT =
  ARCHITECTURE_ARTIFACT_TYPES.length * ARCHITECTURE_LIFECYCLE_PHASES.length;
